// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Group,
  Paper,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString, OperationOutcomeError } from '@medplum/core';
import type { Coverage, Organization, Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import {
  formatUsd,
  getPatientPaymentMethod,
  HSAT_CHARGE_ITEM_DEFINITION_URL,
  INSURED_COPAY_AMOUNT_CENTS,
  isInsuredCopayPatient,
  isSelfPayPatient,
  normalizeUsStateCode,
  resolvePayerClass,
  resolvePrice,
  SELF_PAY_BUNDLE_CHARGE_ITEM_DEFINITION_URL,
  US_STATE_SELECT_OPTIONS,
} from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { botIdentifier } from '../lib/bot-identifier';
import { AddCardModal } from './settings/AddCardModal';
import {
  getCoverageClassName,
  getCoverageClassValue,
  getCoveragePhone,
  stripSurroundingQuotes,
} from './settings/settings.utils';

const BRAND = '#640040';

const couponMessages: Record<string, string> = {
  invalid_code: 'Coupon code not found',
  expired: 'This coupon has expired',
  expired_or_inactive: 'This coupon is no longer active',
  not_self_pay: 'This coupon is not available for your account',
  amount_too_low: 'Discount makes amount too low to charge',
  unsupported_discount_type: 'Coupon type not supported',
};

interface AppliedDiscountUi {
  code: string;
  percentOff: number;
  originalAmountCents: number;
  discountedAmountCents: number;
}

export function OrderHSATPage(): JSX.Element {
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient;

  const [patient, setPatient] = useState<Patient>(profile);
  const selfPay = isSelfPayPatient(patient);
  const insuredCopay = isInsuredCopayPatient(patient);
  const bundleFlow = selfPay || insuredCopay;
  const [addCardOpen, setAddCardOpen] = useState(false);

  const [chargeAmount, setChargeAmount] = useState<number | undefined>();
  const [chargeAmountError, setChargeAmountError] = useState<string | undefined>();

  const [coverage, setCoverage] = useState<Coverage | undefined>();
  const [payorName, setPayorName] = useState('');

  const [editingAddress, setEditingAddress] = useState(false);
  const [editStreet, setEditStreet] = useState('');
  const [editUnit, setEditUnit] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editZip, setEditZip] = useState('');
  const [editState, setEditState] = useState('');
  const [savingAddress, setSavingAddress] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState<AppliedDiscountUi | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);

  const addr = profile.address?.[0];
  const [shippingStreet, setShippingStreet] = useState(addr?.line?.[0] ?? '');
  const [shippingUnit, setShippingUnit] = useState(addr?.line?.[1] ?? '');
  const [shippingCity, setShippingCity] = useState(addr?.city ?? '');
  const [shippingZip, setShippingZip] = useState(addr?.postalCode ?? '');
  const [shippingState, setShippingState] = useState(normalizeUsStateCode(addr?.state) ?? addr?.state ?? '');

  useEffect(() => {
    setPatient(profile);
  }, [profile]);

  useEffect(() => {
    const patientId = profile.id;
    if (!patientId) {
      return;
    }
    medplum
      .searchResources('Coverage', { beneficiary: `Patient/${patientId}` })
      .then(async (coverages) => {
        const first = coverages[0];
        if (!first) {
          return;
        }
        setCoverage(first);
        const payorRef = first.payor?.[0]?.reference;
        if (payorRef) {
          try {
            const org = await medplum.readReference({ reference: payorRef } as { reference: string });
            setPayorName((org as Organization).name ?? '');
          } catch {
            // payor org may not be readable
          }
        }
      })
      .catch(console.error);
  }, [medplum, profile.id]);

  useEffect(() => {
    const definitionUrl = bundleFlow ? SELF_PAY_BUNDLE_CHARGE_ITEM_DEFINITION_URL : HSAT_CHARGE_ITEM_DEFINITION_URL;
    medplum
      .searchResources('ChargeItemDefinition', `url=${definitionUrl}`)
      .then((results) => {
        const cid = results[0];
        const amount = cid ? resolvePrice(cid, resolvePayerClass(patient, new Date().toISOString())) : undefined;
        if (typeof amount === 'number' && amount > 0) {
          setChargeAmount(insuredCopay ? INSURED_COPAY_AMOUNT_CENTS / 100 : amount);
          setChargeAmountError(undefined);
        } else {
          setChargeAmountError('Price unavailable');
        }
      })
      .catch((err: unknown) => {
        console.error(err);
        setChargeAmountError('Price unavailable');
      });
  }, [medplum, bundleFlow, insuredCopay, patient]);

  const extractBotErrorMessage = (err: unknown): string => {
    if (err instanceof OperationOutcomeError) {
      const detail = err.outcome.issue?.[0]?.details?.text ?? '';
      try {
        const parsed = JSON.parse(detail) as { errorMessage?: string };
        if (parsed.errorMessage) {
          return parsed.errorMessage;
        }
      } catch {
        // not JSON, fall through
      }
    }
    return normalizeErrorString(err);
  };

  const handleApplyCoupon = async (): Promise<void> => {
    if (!serviceRequestId || !code.trim()) {
      return;
    }
    setValidating(true);
    setCouponError(null);
    try {
      const result = (await medplum.executeBot(botIdentifier('validate-coupon'), {
        code: code.trim(),
        serviceRequestId,
      })) as
        | { valid: true; code: string; percentOff: number; originalAmountCents: number; discountedAmountCents: number }
        | { valid: false; reason: string };
      if (result.valid) {
        setApplied({
          code: result.code,
          percentOff: result.percentOff,
          originalAmountCents: result.originalAmountCents,
          discountedAmountCents: result.discountedAmountCents,
        });
      } else {
        const message = couponMessages[result.reason] ?? 'Could not apply coupon';
        setCouponError(message);
        showNotification({ color: 'red', title: 'Coupon error', message });
      }
    } catch (err) {
      Sentry.captureException(err);
      const message = normalizeErrorString(err);
      setCouponError(message);
      showNotification({ color: 'red', title: 'Coupon error', message });
    } finally {
      setValidating(false);
    }
  };

  const handleRemoveCoupon = (): void => {
    setApplied(null);
    setCode('');
    setCouponError(null);
  };

  const handleFinishOrder = async (): Promise<void> => {
    if (!serviceRequestId || !profile.id) {
      return;
    }
    setIsSubmitting(true);
    try {
      await Sentry.startSpan({ op: 'ui.click', name: 'Order HSAT' }, async () => {
        await medplum.executeBot(botIdentifier('order-hsat'), {
          patientId: profile.id,
          serviceRequestId,
          ...(applied ? { promotionCode: applied.code } : {}),
        });
      });
      showNotification({ color: 'green', title: 'Order Placed', message: 'Your HSAT has been ordered successfully.' });
      navigate(`/my-chart`);
    } catch (err) {
      Sentry.captureException(err);
      showNotification({ color: 'red', title: 'Error', message: extractBotErrorMessage(err) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const effectiveStreet = editingAddress ? editStreet : shippingStreet;
  const effectiveCity = editingAddress ? editCity : shippingCity;
  const effectiveZip = editingAddress ? editZip : shippingZip;
  const effectiveState = editingAddress ? editState : shippingState;
  const hasValidAddress = Boolean(effectiveStreet && effectiveCity && effectiveZip && effectiveState);

  const planName = coverage ? getCoverageClassName(coverage, 'plan') : '';
  const memberId = coverage ? stripSurroundingQuotes(coverage.subscriberId ?? '') : '';
  const groupId = coverage ? getCoverageClassValue(coverage, 'group') : '';
  const insurancePhone = coverage ? getCoveragePhone(coverage) : '';

  const paymentMethod = getPatientPaymentMethod(patient);

  return (
    <Stack gap="md" p="md" maw={1200} mx="auto" w="100%">
      {/* Breadcrumb + Title */}
      <Paper p="md" radius="md" withBorder shadow="xs">
        <Stack gap={4}>
          <Text fw={700} size="xs" c={BRAND} style={{ cursor: 'pointer' }} onClick={() => navigate(`/my-chart`)}>
            {'My Chart >'}
          </Text>
          <Title order={1} fz={40} fw={700}>
            Sleep Apnea Testing
          </Title>
        </Stack>
      </Paper>

      {/* Order form */}
      <Paper p="md" radius="md" withBorder shadow="sm">
        <Stack gap="lg">
          <Title order={2} fz={32} fw={700}>
            Order Your Test
          </Title>

          {/* Step One: Shipping Address */}
          <Stack gap={4}>
            <Title order={3} fz={24} fw={700}>
              Step One: Confirm Shipping Address
            </Title>
            <Text size="md">
              This is the address we have on file for you. Please confirm that you can receive mail at this address.
            </Text>
          </Stack>
          <Grid gutter="md">
            <Grid.Col span={6}>
              <TextInput
                label="Street Address"
                value={editingAddress ? editStreet : shippingStreet}
                disabled={!editingAddress}
                onChange={(e) => setEditStreet(e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Address 2"
                value={editingAddress ? editUnit : shippingUnit}
                disabled={!editingAddress}
                onChange={(e) => setEditUnit(e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="City"
                value={editingAddress ? editCity : shippingCity}
                disabled={!editingAddress}
                onChange={(e) => setEditCity(e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput
                label="Zipcode"
                value={editingAddress ? editZip : shippingZip}
                disabled={!editingAddress}
                onChange={(e) => setEditZip(e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <Select
                label="State"
                data={US_STATE_SELECT_OPTIONS}
                value={editingAddress ? editState : shippingState}
                disabled={!editingAddress}
                onChange={(val) => setEditState(val ?? '')}
                searchable
              />
            </Grid.Col>
          </Grid>
          <Group>
            {editingAddress ? (
              <>
                <Button
                  style={{ backgroundColor: BRAND, height: 50, paddingInline: 24 }}
                  loading={savingAddress}
                  onClick={async () => {
                    setSavingAddress(true);
                    try {
                      const addressLines = [editStreet, editUnit].filter(Boolean);
                      await medplum.updateResource({
                        ...profile,
                        address: [
                          {
                            ...(profile.address?.[0] ?? {}),
                            line: addressLines.length > 0 ? addressLines : undefined,
                            city: editCity || undefined,
                            state: editState || undefined,
                            postalCode: editZip || undefined,
                            country: 'USA',
                          },
                        ],
                      });
                      setShippingStreet(editStreet);
                      setShippingUnit(editUnit);
                      setShippingCity(editCity);
                      setShippingZip(editZip);
                      setShippingState(editState);
                      setEditingAddress(false);
                      showNotification({ color: 'green', message: 'Address updated.' });
                    } catch (err) {
                      showNotification({ color: 'red', message: normalizeErrorString(err) });
                    } finally {
                      setSavingAddress(false);
                    }
                  }}
                >
                  Save Address
                </Button>
                <Button
                  variant="outline"
                  style={{ borderColor: BRAND, color: BRAND, height: 50, paddingInline: 24 }}
                  disabled={savingAddress}
                  onClick={() => setEditingAddress(false)}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                style={{ backgroundColor: BRAND, height: 50, paddingInline: 24 }}
                onClick={() => {
                  setEditStreet(shippingStreet);
                  setEditUnit(shippingUnit);
                  setEditCity(shippingCity);
                  setEditZip(shippingZip);
                  setEditState(shippingState);
                  setEditingAddress(true);
                }}
              >
                Edit Address
              </Button>
            )}
          </Group>

          <Box h={20} />

          {/* Step Two: Insurance */}
          <Stack gap={4}>
            <Title order={3} fz={24} fw={700}>
              Step Two: Confirm Insurance
            </Title>
            <Text size="md">
              This is the insurance information we have on file for you. Please confirm that it is up to date or add a
              co-insurance plan to supplement your coverage.
            </Text>
          </Stack>
          <Grid gutter="md">
            <Grid.Col span={6}>
              <TextInput label="Plan Name" value={planName} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Plan Carrier" value={payorName} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Member ID" value={memberId} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Group ID" value={groupId} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label="Insurance Contact" value={insurancePhone} disabled />
            </Grid.Col>
          </Grid>
          <Group>
            <Button
              style={{ backgroundColor: BRAND, height: 50, paddingInline: 24 }}
              onClick={() => navigate('/settings')}
            >
              Edit Insurance
            </Button>
            <Button
              variant="outline"
              style={{ borderColor: BRAND, color: BRAND, height: 50, paddingInline: 24 }}
              disabled
            >
              Add Another Insurance
            </Button>
          </Group>

          <Box h={20} />

          {/* Step Three: Copay */}
          <Stack gap="xs">
            <Title order={3} fz={24} fw={700}>
              Step Three: Process Copay
            </Title>
            <Group gap="md">
              <Box px="xl" py="xs" style={{ borderRadius: 4, backgroundColor: 'var(--polaris-light-purple)' }}>
                {chargeAmount != null && (
                  <Text fw={600} size="md">
                    Amount Due: {formatUsd(chargeAmount)}
                  </Text>
                )}
                {selfPay && chargeAmount != null && (
                  <Text size="sm" c="dimmed">
                    This one-time amount covers your intake, your sleep test, and your results visit. Payment will be
                    requested separately.
                  </Text>
                )}
                {insuredCopay && chargeAmount != null && (
                  <Text size="sm" c="dimmed">
                    Your $50 copay covers your intake, your sleep test, and your results visit.
                  </Text>
                )}
                {chargeAmount == null && chargeAmountError && (
                  <Text fw={600} size="md" c="red">
                    Price unavailable
                  </Text>
                )}
                {chargeAmount == null && !chargeAmountError && <Skeleton w={180} h={20} />}
              </Box>
            </Group>
            {chargeAmountError && (
              <Alert color="red" variant="light">
                We can't load the price for this test right now. Please refresh the page or try again later.
              </Alert>
            )}
          </Stack>

          <Paper p="md" radius="md" withBorder>
            <Stack gap="md">
              {applied ? (
                <Group justify="space-between">
                  <Text c="green" fw={600}>
                    Code APPLIED: {applied.code}
                  </Text>
                  <Button variant="subtle" onClick={handleRemoveCoupon}>
                    Remove
                  </Button>
                </Group>
              ) : (
                <Stack gap={4}>
                  <Group align="end">
                    <TextInput
                      label="Coupon code"
                      value={code}
                      onChange={(e) => setCode(e.currentTarget.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleApplyCoupon().catch(console.error);
                        }
                      }}
                      disabled={validating}
                      style={{ flex: 1 }}
                    />
                    <Button
                      variant="outline"
                      loading={validating}
                      disabled={!code.trim() || validating}
                      onClick={() => handleApplyCoupon().catch(console.error)}
                    >
                      Apply
                    </Button>
                  </Group>
                  {couponError && (
                    <Text c="red" size="sm">
                      {couponError}
                    </Text>
                  )}
                </Stack>
              )}

              {applied ? (
                <Stack gap={4}>
                  <Group justify="space-between">
                    <Text>Subtotal</Text>
                    <Text>{formatUsd(applied.originalAmountCents / 100)}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text c="green">{`Discount (${applied.code}, ${applied.percentOff}% off)`}</Text>
                    <Text c="green">
                      -{formatUsd((applied.originalAmountCents - applied.discountedAmountCents) / 100)}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Title order={3}>Total Due Today</Title>
                    <Title order={3}>{formatUsd(applied.discountedAmountCents / 100)}</Title>
                  </Group>
                </Stack>
              ) : null}
            </Stack>
          </Paper>

          <Paper bg="gray.0" p="md" radius="md" withBorder>
            <Stack gap="md">
              <Divider
                label={
                  <Text fw={700} size="lg">
                    Card Details
                  </Text>
                }
                labelPosition="left"
              />
              {paymentMethod ? (
                <>
                  <Grid gutter="md">
                    <Grid.Col span={6}>
                      <TextInput label="Card brand" value={paymentMethod.brand} disabled />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <TextInput label="Last 4" value={paymentMethod.last4} disabled />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <TextInput label="Cardholder name" value={paymentMethod.cardholderName} disabled />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <TextInput
                        label="Expiration"
                        value={`${String(paymentMethod.expMonth).padStart(2, '0')}/${paymentMethod.expYear}`}
                        disabled
                      />
                    </Grid.Col>
                  </Grid>
                  <Group justify="flex-start">
                    <Button
                      variant="outline"
                      style={{ borderColor: BRAND, color: BRAND, height: 50, paddingInline: 24 }}
                      onClick={() => setAddCardOpen(true)}
                    >
                      Edit via Stripe
                    </Button>
                  </Group>
                </>
              ) : (
                <Group justify="space-between" align="center">
                  <Text size="sm">No card on file. Payment will be requested separately.</Text>
                  <Button
                    style={{ backgroundColor: BRAND, height: 50, paddingInline: 24 }}
                    onClick={() => setAddCardOpen(true)}
                  >
                    Add card
                  </Button>
                </Group>
              )}
            </Stack>
          </Paper>

          {patient.id && (
            <AddCardModal
              opened={addCardOpen}
              patientId={patient.id}
              onClose={() => setAddCardOpen(false)}
              onSaved={(updated) => {
                setPatient(updated);
                setAddCardOpen(false);
              }}
            />
          )}

          {/* Acknowledgment */}
          <Paper p="md" radius="md" withBorder>
            <Stack gap="md">
              <Text size="sm">
                I acknowledge that the above copay amount is based on information available at time of service and that
                the estimated amount covered by my insurance provider may differ from the actual amount upon submission
                and processing of the claim. I agree that I will be responsible for any remaining balance not covered by
                my insurance provider after the claim has been processed.
              </Text>
              <Checkbox
                label="By checking this box, I agree to the above terms and conditions of ordering this test."
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.currentTarget.checked)}
                color={BRAND}
              />
            </Stack>
          </Paper>

          {!hasValidAddress && (
            <Alert color="red" variant="light">
              A complete shipping address (street, city, zip, and state) is required to place an order. Please fill in
              your address above.
            </Alert>
          )}
          <Box>
            <Button
              style={{ backgroundColor: BRAND, height: 50, paddingInline: 24 }}
              loading={isSubmitting}
              disabled={!acknowledged || !hasValidAddress || (chargeAmount == null && !applied) || isSubmitting}
              onClick={() => handleFinishOrder()}
            >
              Finish Order
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Stack>
  );
}
